import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import {
  hasProfileSubmittedKyc,
  isProfileKycVerified,
  isProfileVerificationPending,
  needsProfileKycVerification,
} from "@/lib/kycStatus";

/**
 * useUserProfile — Single source of truth for the current user's profile.
 *
 * IMPORTANT: All components that need verification status, avatar, gender, etc.
 * must use this hook (or the same query key) so React Query shares the same
 * cache entry. This prevents the "verified user briefly shown as unverified"
 * flapping caused by multiple parallel queries with different staleTimes.
 */
export function useUserProfile() {
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin, loading: rolesLoading } = useUserRoles();

  const query = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, is_verified, is_admin_verified, admin_bypass_kyc, verification_status, verification_submitted_at, selfie_url, avatar_url, gender, full_name, account_type, iban, iban_confirmed_at"
        )
        .eq("id", user.id)
        .maybeSingle();
      if (error) return null;
      return data;
    },
    enabled: !!user?.id,
    // Long staleTime to avoid races between components.
    staleTime: 1000 * 60 * 5,
    // Keep last data while refetching to prevent UI from flashing "unverified".
    placeholderData: (previous) => previous,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  const profile = query.data;
  const isLoading = query.isLoading || rolesLoading;
  const hasData = !isLoading && !!profile;

  // Admins/super admins never need KYC verification.
  const isPrivilegedAdmin = isAdmin || isSuperAdmin;

  // Single source of truth: any verified flag, admin bypass, privileged admin,
  // or approved/verified KYC status means the account is verified everywhere.
  const isVerified = isProfileKycVerified(profile, { isPrivilegedAdmin });
  const isKycApproved = isVerified;

  const hasSubmittedVerification = hasProfileSubmittedKyc(profile);
  const verificationPending = isProfileVerificationPending(profile);

  // needsVerification is ONLY true when data is loaded AND the account is
  // clearly unverified. Any verified flag (isVerified) suppresses it to
  // guarantee approved accounts never see the KYC modal/pulse.
  const needsVerification = needsProfileKycVerification(profile, {
    hasUser: !!user && hasData,
    isLoading,
    isPrivilegedAdmin,
  });

  return {
    profile,
    isLoading,
    isVerified,
    isKycApproved,
    hasSubmittedVerification,
    verificationPending,
    needsVerification,
    isPrivilegedAdmin,
  };
}
